// =================================================================
// ActiveUsersPanel.js — Session 36
// Indicateur de charge live par utilisateur connecté
// Utilisé par BenDashboard et InfrastructureDashboard
// =================================================================

import React, { useState, useEffect, useRef } from 'react';
import { Users, Activity, PauseCircle, PlayCircle, DollarSign, Radio } from 'lucide-react';
import { toast, confirm as confirmModal } from '../ui/Toast';

const REFRESH_INTERVAL_MS = 30000;

const LOAD_COLORS = {
  normal: { bg: 'rgba(16,185,129,0.1)', dot: '#10b981', label: 'Normal' },
  warning: { bg: 'rgba(245,158,11,0.1)', dot: '#f59e0b', label: 'Charge moyenne' },
  critical: { bg: 'rgba(239,68,68,0.1)', dot: '#ef4444', label: 'Charge critique' }
};

function formatTimeAgo(date) {
  if (!date) return '—';
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'à l\'instant';
  if (mins < 60) return `il y a ${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  return new Date(date).toLocaleDateString('fr-FR');
}

const ActiveUsersPanel = ({ compact = false, showSuspendButton = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [suspendMinutes, setSuspendMinutes] = useState(15);
  const intervalRef = useRef(null);

  const token = localStorage.getItem('oracle_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadActiveUsers = async () => {
    try {
      const res = await fetch('/api/admin/active-users', { headers });
      if (res.ok) {
        const json = await res.json();
        setData(json);
        setLastUpdate(new Date());
      }
    } catch (e) { /* silencieux — le panel reste affiché avec données précédentes */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadActiveUsers();
    intervalRef.current = setInterval(loadActiveUsers, REFRESH_INTERVAL_MS);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const submitSuspend = async (userId, username, minutes) => {
    try {
      const res = await fetch(`/api/admin/users/${userId}/suspend`, {
        method: 'POST', headers,
        body: JSON.stringify({ duration_minutes: minutes })
      });
      const json = await res.json();
      if (res.ok) {
        const graceSec = json.grace_seconds || 30;
        toast.success(`Notice envoyée à ${username} — suspension effective dans ${graceSec}s (${minutes} min)`);
        setSuspendTarget(null);
        loadActiveUsers();
      } else {
        toast.error(json.error || 'Erreur suspension');
      }
    } catch (e) { toast.error('Erreur réseau'); }
  };

  const unsuspend = async (userId, username) => {
    if (!(await confirmModal(`Lever la suspension de ${username} ?`))) return;
    try {
      const res = await fetch(`/api/admin/users/${userId}/suspend`, { method: 'DELETE', headers });
      if (res.ok) {
        toast.success(`Fin des opérations, actualisation...`);
        loadActiveUsers();
      }
    } catch (e) { toast.error('Erreur réseau'); }
  };

  const isSuspended = (u) => u.suspended_until && new Date(u.suspended_until) > new Date();

  return (
    <div style={{
      background: 'var(--card-bg)',
      border: '1px solid var(--card-border)',
      borderRadius: '12px',
      padding: '16px 20px',
      marginBottom: compact ? '16px' : '24px'
    }}>
      <style>{`
        @keyframes activePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.25); }
        }
        .active-dot { animation: activePulse 2s ease-in-out infinite; }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Radio size={18} style={{ color: '#10b981' }} className="active-dot" />
          <div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text)' }}>
              Utilisateurs en ligne
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              {data?.connected_count ?? data?.active_count ?? 0} connecté{((data?.connected_count ?? data?.active_count ?? 0)) > 1 ? 's' : ''}
              {data?.suspended_count > 0 && (
                <> · <span style={{ color: '#f59e0b', fontWeight: 600 }}>{data.suspended_count} suspendu{data.suspended_count > 1 ? 's' : ''}</span></>
              )}
              {' · Refresh 30s'}
              {lastUpdate && ` · ${lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`}
            </div>
          </div>
        </div>
        <button
          onClick={loadActiveUsers}
          disabled={loading}
          style={{
            padding: '6px 12px', fontSize: '11px', fontWeight: '600',
            background: 'var(--input-bg)', border: '1px solid var(--card-border)',
            borderRadius: '6px', color: 'var(--text-secondary)', cursor: 'pointer'
          }}
          title="Rafraîchir maintenant"
        >
          <Activity size={12} style={{ display: 'inline', marginRight: '4px' }} />
          Refresh
        </button>
      </div>

      {/* Liste */}
      {loading && !data ? (
        <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '12px' }}>
          Chargement…
        </div>
      ) : !data?.users?.length ? (
        <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '12px' }}>
          Aucun utilisateur connecté actuellement
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {data.users.map(u => {
            const load = LOAD_COLORS[u.load_level] || LOAD_COLORS.normal;
            const suspended = isSuspended(u);
            return (
              <div key={u.id} style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '10px 12px',
                background: suspended ? 'rgba(245,158,11,0.05)' : 'var(--input-bg)',
                border: `1px solid ${suspended ? 'rgba(245,158,11,0.3)' : 'var(--card-border)'}`,
                borderRadius: '8px'
              }}>
                {/* Dot charge */}
                <div style={{
                  width: '10px', height: '10px', borderRadius: '50%',
                  background: suspended ? '#f59e0b' : load.dot,
                  flexShrink: 0,
                  boxShadow: `0 0 0 3px ${suspended ? 'rgba(245,158,11,0.15)' : load.bg}`
                }} className={suspended ? '' : 'active-dot'} />

                {/* Identité */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text)' }}>
                      {u.full_name || u.username}
                    </span>
                    <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>
                      @{u.username} · {u.role}
                    </span>
                    {suspended && (
                      <span style={{
                        fontSize: '10px', padding: '1px 6px', borderRadius: '4px',
                        background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontWeight: '600'
                      }}>
                        Suspendu → {new Date(u.suspended_until).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                    Sessions: {u.active_sessions} · Dernier login: {formatTimeAgo(u.last_login)}
                  </div>
                </div>

                {/* Métriques charge */}
                <div style={{ display: 'flex', gap: '12px', fontSize: '11px' }}>
                  <div style={{ textAlign: 'right', minWidth: '60px' }}>
                    <div style={{ fontWeight: '700', color: load.dot }}>{u.calls_5min}</div>
                    <div style={{ fontSize: '9px', color: 'var(--text-secondary)' }}>appels / 5min</div>
                  </div>
                  <div style={{ textAlign: 'right', minWidth: '60px' }}>
                    <div style={{ fontWeight: '700', color: 'var(--text)' }}>
                      <DollarSign size={10} style={{ display: 'inline', verticalAlign: 'middle' }} />
                      {(u.cost_today_usd || 0).toFixed(3)}
                    </div>
                    <div style={{ fontSize: '9px', color: 'var(--text-secondary)' }}>coût 24h</div>
                  </div>
                </div>

                {/* Action rapide suspend */}
                {showSuspendButton && (
                  suspended ? (
                    <button
                      onClick={() => unsuspend(u.id, u.username)}
                      style={{
                        padding: '6px 10px', fontSize: '11px', fontWeight: '600',
                        background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
                        color: '#10b981', borderRadius: '6px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '4px'
                      }}
                      title="Lever la suspension"
                    >
                      <PlayCircle size={12} /> Lever
                    </button>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      {suspendTarget === u.id ? (
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          <select
                            value={suspendMinutes}
                            onChange={e => setSuspendMinutes(parseInt(e.target.value, 10))}
                            style={{
                              padding: '4px 6px', fontSize: '11px',
                              background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                              borderRadius: '4px', color: 'var(--text)'
                            }}
                          >
                            <option value={15}>15 min</option>
                            <option value={30}>30 min</option>
                            <option value={60}>1h</option>
                            <option value={120}>2h</option>
                          </select>
                          <button
                            onClick={() => submitSuspend(u.id, u.username, suspendMinutes)}
                            style={{
                              padding: '4px 8px', fontSize: '11px', fontWeight: '600',
                              background: '#f59e0b', color: 'white', border: 'none',
                              borderRadius: '4px', cursor: 'pointer'
                            }}
                          >
                            OK
                          </button>
                          <button
                            onClick={() => setSuspendTarget(null)}
                            style={{
                              padding: '4px 6px', fontSize: '11px',
                              background: 'transparent', color: 'var(--text-secondary)', border: 'none',
                              cursor: 'pointer'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => { setSuspendTarget(u.id); setSuspendMinutes(15); }}
                          style={{
                            padding: '6px 10px', fontSize: '11px', fontWeight: '600',
                            background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)',
                            color: '#f59e0b', borderRadius: '6px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '4px'
                          }}
                          title="Suspendre temporairement"
                        >
                          <PauseCircle size={12} /> Pause
                        </button>
                      )}
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Légende seuils */}
      {data?.thresholds && !compact && (
        <div style={{
          marginTop: '12px', paddingTop: '10px',
          borderTop: '1px solid var(--card-border)',
          display: 'flex', gap: '14px', fontSize: '10px', color: 'var(--text-secondary)'
        }}>
          <span><span style={{ color: '#10b981' }}>●</span> Normal (&lt; {data.thresholds.warn} appels/5min)</span>
          <span><span style={{ color: '#f59e0b' }}>●</span> Moyenne ({data.thresholds.warn}-{data.thresholds.crit - 1})</span>
          <span><span style={{ color: '#ef4444' }}>●</span> Critique (≥ {data.thresholds.crit})</span>
        </div>
      )}
    </div>
  );
};

export default ActiveUsersPanel;
